import { useAtomValue } from 'jotai';

import type {
  TProviderTreatment,
  TTreatmentType,
} from '@/react-query/insights/types';
import { useGetClinicalProviderInsightsData } from '@/react-query/insights/useGetClinicalProviderInsightsData';
import { usePreferences } from '@/react-query/usePreferences';

import { endDateAtom, startDateAtom, txTypeAtom } from '../../atoms';

const useTreatmentsFilter = ({ data }: { data?: TProviderTreatment[] }) => {
  const selectedTreatments = useAtomValue(txTypeAtom);

  const { data: prefs } = usePreferences();
  const enabledTreatments = prefs?.insightsEnabledRecos;

  // List of treatments to show
  const treatments = enabledTreatments
    ?.filter(
      (treatment) =>
        !selectedTreatments.length || selectedTreatments?.includes(treatment),
    )
    ?.reduce((acc, treatment) => {
      if (!acc.includes(treatment)) {
        acc.push(treatment);
      }
      return acc;
    }, [] as TTreatmentType[]);

  const filteredData = data?.filter((treatment) =>
    treatments?.includes(treatment?.suggestionType as TTreatmentType),
  );

  return filteredData;
};

export const useGetFilteredClinicalTreatments = ({
  providerId,
  practiceId,
}: {
  providerId: number;
  practiceId?: number;
}) => {
  const startDate = useAtomValue(startDateAtom);
  const endDate = useAtomValue(endDateAtom);

  const { data, isLoading: isClinicalLoading } =
    useGetClinicalProviderInsightsData({
      providerId,
      start: startDate,
      end: endDate,
      practiceId,
    });

  const filteredData = useTreatmentsFilter({ data });

  const curatedData = filteredData?.map((treatment) => {
    return {
      ...treatment,
      isCurated:
        (treatment.hasDecay &&
          treatment.dmf &&
          treatment.dmf >= 0.4 &&
          (treatment.deepestCariesDepth === 'into_pulp' ||
            treatment.deepestCariesDepth === 'into_crown_dentin') &&
          (treatment.suggestionType === 'Crown' ||
            treatment.suggestionType === 'Root Canal' ||
            treatment.suggestionType === 'Extraction')) ||
        false,
    };
  });

  return {
    data: curatedData ?? [],
    isLoading: isClinicalLoading,
  };
};
